import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import {
  getCatalogoFarmacosControllerListarQueryKey,
  getCatalogoFarmacosControllerObterQueryKey,
  useCatalogoFarmacosControllerAtualizarApresentacao,
  useCatalogoFarmacosControllerCriarApresentacao,
} from '@/api/generated/admin-catalogo-farmacos/admin-catalogo-farmacos';
import type { ApresentacaoResponse } from '@/api/generated/model';
import { aplicarErrosDoServidor } from '@/api/erros';
import {
  paraNumero,
  paraTexto,
  previaDoRotulo,
  problemaDaConcentracao,
} from '@/features/admin/catalogo-farmacos/concentracao';
import { Campo, CLASSE_CONTROLE } from '@/shared/components/Campo';
import { Alert, AlertDescription } from '@/shared/ui/alert';
import { Button } from '@/shared/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import { Input } from '@/shared/ui/input';
import { toast } from '@/shared/ui/sonner';

/**
 * ⚠️ A regra do XOR mora no `superRefine`, e não em dois `refine` de campo:
 * ela é sobre o PAR, e o zod só consegue olhar os dois juntos no objeto.
 */
const schema = z
  .object({
    nomeComercial: z.string().trim().min(1, 'Informe o nome comercial').max(200),
    fabricante: z.string().trim().max(200),
    concentracaoValor: z.string().trim(),
    concentracaoUnidade: z.string().trim().max(20),
  })
  .superRefine((dados, ctx) => {
    const problema = problemaDaConcentracao(dados.concentracaoValor, dados.concentracaoUnidade);
    if (problema) {
      ctx.addIssue({ code: 'custom', path: [problema.campo], message: problema.mensagem });
    }
  });

type Formulario = z.infer<typeof schema>;

interface Props {
  aberto: boolean;
  aoFechar: () => void;
  farmacoId: string;
  /** Ausente = criação. */
  apresentacao?: ApresentacaoResponse;
}

/**
 * Criar/editar a apresentação comercial ("Vetoryl 30 mg").
 *
 * ⚠️ **Concentração é valor E unidade, ou nenhum dos dois** — o servidor recusa
 * a metade com 422. A checagem acontece antes do envio (`concentracao.ts`)
 * porque "30" sem "mg" não é meio preenchido: é uma informação que não quer
 * dizer nada, e descobrir isso por um erro depois de salvar é pior do que ser
 * avisado enquanto se digita.
 */
export function ApresentacaoFormDialogo({ aberto, aoFechar, farmacoId, apresentacao }: Props) {
  const queryClient = useQueryClient();
  const editando = Boolean(apresentacao);

  const criar = useCatalogoFarmacosControllerCriarApresentacao();
  const atualizar = useCatalogoFarmacosControllerAtualizarApresentacao();

  const {
    register,
    handleSubmit,
    reset,
    setError,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<Formulario>({ resolver: zodResolver(schema) });

  useEffect(() => {
    if (!aberto) return;
    reset({
      nomeComercial: apresentacao?.nomeComercial ?? '',
      fabricante: apresentacao?.fabricante ?? '',
      concentracaoValor: paraTexto(apresentacao?.concentracaoValor),
      concentracaoUnidade: apresentacao?.concentracaoUnidade ?? '',
    });
  }, [aberto, apresentacao, reset]);

  const previa = previaDoRotulo(
    watch('nomeComercial') ?? '',
    watch('concentracaoValor') ?? '',
    watch('concentracaoUnidade') ?? '',
  );

  const aoEnviar = handleSubmit(async (dados) => {
    const valor = paraNumero(dados.concentracaoValor);
    const unidade = dados.concentracaoUnidade.trim();

    // Os dois juntos, sempre — o `superRefine` já garantiu que não sobra meio
    // par, e `null` nos dois é como se apaga a concentração.
    const comConcentracao = valor !== null && unidade !== '';

    const corpo = {
      nomeComercial: dados.nomeComercial,
      fabricante: dados.fabricante.trim() ? dados.fabricante.trim() : null,
      concentracaoValor: comConcentracao ? valor : null,
      concentracaoUnidade: comConcentracao ? unidade : null,
    };

    try {
      if (apresentacao) {
        await atualizar.mutateAsync({
          id: farmacoId,
          apresentacaoId: apresentacao.id,
          data: corpo,
        });
      } else {
        await criar.mutateAsync({ id: farmacoId, data: corpo });
      }

      // As rotas de apresentação respondem com o FÁRMACO inteiro; é o detalhe
      // que redesenha a sub-lista, e a listagem que reconta `apresentações`.
      await queryClient.invalidateQueries({
        queryKey: getCatalogoFarmacosControllerObterQueryKey(farmacoId),
      });
      await queryClient.invalidateQueries({
        queryKey: getCatalogoFarmacosControllerListarQueryKey(),
      });

      toast.success(editando ? 'Apresentação atualizada.' : 'Apresentação criada.');
      aoFechar();
    } catch (erro) {
      aplicarErrosDoServidor(erro, setError, 'Não foi possível salvar a apresentação.');
    }
  });

  return (
    <Dialog open={aberto} onOpenChange={(v) => !v && aoFechar()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{editando ? 'Editar apresentação' : 'Nova apresentação'}</DialogTitle>
          <DialogDescription>
            A apresentação é a marca com a concentração. É ela que transforma &quot;1
            comprimido&quot; em dose.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={aoEnviar} className="space-y-4" noValidate>
          {errors.root?.message && (
            <Alert variant="destructive">
              <AlertDescription>{errors.root.message}</AlertDescription>
            </Alert>
          )}

          <Campo
            id="nomeComercial"
            rotulo="Nome comercial"
            erro={errors.nomeComercial?.message}
            obrigatorio
          >
            <Input
              id="nomeComercial"
              className={CLASSE_CONTROLE}
              placeholder="Ex.: Vetoryl"
              {...register('nomeComercial')}
            />
          </Campo>

          <Campo id="fabricante" rotulo="Fabricante" erro={errors.fabricante?.message}>
            <Input
              id="fabricante"
              className={CLASSE_CONTROLE}
              placeholder="Ex.: Dechra"
              {...register('fabricante')}
            />
          </Campo>

          <div className="grid grid-cols-2 gap-3">
            <Campo
              id="concentracaoValor"
              rotulo="Concentração"
              erro={errors.concentracaoValor?.message}
            >
              <Input
                id="concentracaoValor"
                inputMode="decimal"
                className={CLASSE_CONTROLE}
                placeholder="Ex.: 30"
                {...register('concentracaoValor')}
              />
            </Campo>

            <Campo
              id="concentracaoUnidade"
              rotulo="Unidade"
              erro={errors.concentracaoUnidade?.message}
            >
              <Input
                id="concentracaoUnidade"
                className={CLASSE_CONTROLE}
                placeholder="Ex.: mg"
                {...register('concentracaoUnidade')}
              />
            </Campo>
          </div>

          <p className="text-xs text-muted-foreground">
            Valor e unidade andam juntos: preencha os dois ou deixe os dois em branco.
            {previa && (
              <>
                {' '}
                No seletor do receituário vai aparecer como{' '}
                <span className="text-foreground font-medium">{previa}</span>.
              </>
            )}
          </p>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={aoFechar} disabled={isSubmitting}>
              Cancelar
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {editando ? 'Salvar' : 'Criar apresentação'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
